import React, { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const Discover = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      return toast.error("Please fill out the required fields.");
    }
    toast.success("Message sent successfully! We'll be in touch soon.");
    setFormData({ name: "", phone: "", email: "", message: "" });
  };

  return (
    <div className="min-h-screen w-full bg-[#010710]">
      <Navbar />
      
      <main>
        {/* Fullscreen Hero Section */}
        <section 
          className="relative flex min-h-[100dvh] w-full items-center overflow-hidden bg-cover bg-center bg-no-repeat pb-10 pt-24"
          style={{ backgroundImage: "url('/images/discover_bg.png')" }}
        >
          {/* Overlay gradient to ensure text readability */}
          <div className="absolute inset-0 bg-[#010710]/40 mix-blend-multiply" />

          <div className="relative mx-auto grid w-full max-w-[1440px] grid-cols-1 items-center gap-8 px-6 lg:grid-cols-[420px_1fr] xl:grid-cols-[460px_1fr] lg:gap-12 xl:gap-16 lg:px-[70px]">
            
            {/* Left Column: Copy & Form (Shrunk to fit screen) */}
            <div className="relative z-10 flex w-full flex-col gap-5">
              
              {/* Text Content */}
              <div className="flex flex-col gap-2">
                <p className="text-[10px] font-bold uppercase tracking-[2px] text-white/80">
                  DISCOVER YOUR AI OPPORTUNITIES
                </p>
                <h1 className="text-[34px] font-bold leading-[1.1] text-white sm:text-[38px] xl:text-[44px]">
                  Where could AI create value in your business?
                </h1>
                <p className="mt-1 text-[13.5px] leading-[22px] text-white/80">
                  Tell us about your business. We'll identify opportunities that are relevant to how you actually work.
                </p>
              </div>

              {/* Form Card - Reduced padding and gaps to fit screen */}
              <div className="w-full rounded-[20px] bg-white p-5 shadow-2xl sm:p-6">
                <div className="mb-4 flex flex-col gap-1">
                  <h2 className="text-[20px] font-bold text-[#0F172A]">Send Message</h2>
                  <p className="text-[12.5px] leading-[18px] text-[#64748B]">
                    Please fill out the form below with your details and message to contact us.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                  {/* Name and Phone Row */}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <input 
                      type="text" 
                      placeholder="Full Name" 
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full rounded-[10px] border border-transparent bg-[#F1F5F9] px-3.5 py-2.5 text-[13px] text-[#0F172A] outline-none transition-colors focus:border-[#155DFC] focus:bg-white focus:ring-1 focus:ring-[#155DFC]"
                    />
                    <div className="flex w-full overflow-hidden rounded-[10px] border border-transparent bg-[#F1F5F9] transition-colors focus-within:border-[#155DFC] focus-within:bg-white focus-within:ring-1 focus-within:ring-[#155DFC]">
                      <div className="flex items-center justify-center border-r border-[#E2E8F0] bg-[#E2E8F0]/50 px-3 py-2.5 text-[13px] font-medium text-[#64748B]">
                        +91
                      </div>
                      <input 
                        type="tel" 
                        placeholder="Phone Number" 
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full bg-transparent px-3 py-2.5 text-[13px] text-[#0F172A] outline-none"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <input 
                    type="email" 
                    placeholder="Email Address" 
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full rounded-[10px] border border-transparent bg-[#F1F5F9] px-3.5 py-2.5 text-[13px] text-[#0F172A] outline-none transition-colors focus:border-[#155DFC] focus:bg-white focus:ring-1 focus:ring-[#155DFC]"
                  />

                  {/* Message */}
                  <textarea 
                    placeholder="How can we help you?" 
                    rows={3}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full resize-none rounded-[10px] border border-transparent bg-[#F1F5F9] px-3.5 py-2.5 text-[13px] text-[#0F172A] outline-none transition-colors focus:border-[#155DFC] focus:bg-white focus:ring-1 focus:ring-[#155DFC]"
                  />
                  
                  {/* Submit Button */}
                  <div className="mt-1 flex justify-end">
                    <button 
                      type="submit" 
                      className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#155DFC] px-6 py-2.5 text-[13.5px] font-semibold text-white transition-opacity hover:opacity-90"
                    >
                      Submit <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
                    </button>
                  </div>
                </form>
              </div>

            </div>

            {/* Right Column: Constrained height so it fits naturally */}
            <div className="relative z-0 flex w-full justify-center lg:justify-end">
              <div className="relative w-full max-w-[850px]">
                <img 
                  src="/images/discover_img.png" 
                  alt="AI Opportunities Map" 
                  className="w-full max-h-[85vh] object-contain origin-right" 
                />
              </div>
            </div>

          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default Discover;